
import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable, combineLatest, map, of, shareReplay } from 'rxjs';

import { FilterService } from '../../../../../Common/Filter/services/abstract-filter.service';
import { PaginatorServiceInterface } from '../../../../../Common/Misc/services/paginator.service.interface';
import { SortOrderServiceInterface } from '../../../../../Common/Misc/services/sort-order.service.interface';
import { PageCriteriaModelInterface } from '../../../../../Common/Misc/models/page-criteria.model.interface';
import { EmailRecipientModel } from '../../../../../Common/TargetingShared/model/statisticsReport/email-recipient.model';
import { IndividualRecipientReportDataModel } from '../../../../../Common/TargetingShared/components/statisticReport/RecipientReport/recipient.report.data.model';
import { LocalizedEnum } from '../../../../../api/journodex/model/localized-enum';
import { StatisticsReportService } from '../../../../../Common/TargetingShared/services/statistics.report.service';
import {
    DistributionReportStat,
    distributionReportRelevantStatHierarchy
} from '../../../../../Common/TargetingShared/model/statisticsReport/distribution-report-stat-api.model';
import { FilterTypeTextModel } from '../../../../../Common/Filter/models/filter-type-text.model';
import { SortOrderEnum } from '../../../../../api/shared/enums/sort-order.enum';

enum EmailRecipientsSortFields {
    NAME = 'NAME',
    STATISTIC = 'STATISTIC'
}

enum EmailRecipientsFilters {
    EMAIL = 'EMAIL',
}

@Injectable()
export class EmailRecipientSearchService extends FilterService implements PaginatorServiceInterface, SortOrderServiceInterface {
    private searchLoadingSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    searchLoading$ = this.searchLoadingSubject.asObservable();

    private pageCriteriaSubject: BehaviorSubject<PageCriteriaModelInterface> = new BehaviorSubject<PageCriteriaModelInterface>( {page: 1, pageSize: 25});

    sortFields: LocalizedEnum[] = [
        {
            enumValue: EmailRecipientsSortFields.NAME,
            displayString: 'Briefings.recipients.sort.name'
        },
        {
            enumValue: EmailRecipientsSortFields.STATISTIC,
            displayString: 'Briefings.recipients.sort.statistic'
        }
    ];

    protected emailFilter: FilterTypeTextModel = new FilterTypeTextModel(
        EmailRecipientsFilters.EMAIL,
        'Briefings.recipients.filter.email',
        [],
        'Briefings.recipients.filter.email.placeholder'
    );

    private sortFieldSubject: BehaviorSubject<LocalizedEnum> = new BehaviorSubject<LocalizedEnum>(this.sortFields[0]);
    private sortOrderSubject: BehaviorSubject<SortOrderEnum> = new BehaviorSubject<SortOrderEnum>(SortOrderEnum.ascending);
    private statTypeSubject: BehaviorSubject<DistributionReportStat> = new BehaviorSubject<DistributionReportStat>(DistributionReportStat.SENT);
    private selectedIndividualRecipientSubject: BehaviorSubject<IndividualRecipientReportDataModel> =
        new BehaviorSubject<IndividualRecipientReportDataModel>(null);

    private nResultSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
    results$: Observable<EmailRecipientModel[]> = combineLatest([
        this.statisticsReportService.recipientReportData$,
        this.sortFieldSubject,
        this.sortOrderSubject,
        this.pageCriteriaSubject,
        this.filters$,
        this.statTypeSubject,
        this.selectedIndividualRecipientSubject
    ]).pipe(
        map(([report, sortField, sortOrder, pageCriteria, filters, statType, selectedIndividualRecipient]) => {
            this.searchLoadingSubject.next(false);
            let res = selectedIndividualRecipient?.emailRecipients
                ?? (report?.emailRecipients ? report.emailRecipients : []);

            // deal with statType
            res = this.filterByStatType(res, statType);

            // deal with filtering
            const emailFilterValues = this.emailFilter.getValue();
            if (emailFilterValues?.length > 0) {
                res = res.filter(item => {
                    let containKeyword = true;
                    emailFilterValues.forEach(keyword => {
                        if (!item.email.toLowerCase().includes(keyword.toLowerCase())) {
                            containKeyword = false;
                        }
                    });
                    return containKeyword;
                });
            }

            this.nResultSubject.next(res.length);

            // deal with sorting
            res.sort((a, b) => a.email.toLocaleLowerCase().localeCompare(b.email.toLocaleLowerCase()));
            switch (sortField.enumValue) {
                case EmailRecipientsSortFields.STATISTIC:
                // Statistic
                    if (sortOrder === SortOrderEnum.ascending) {
                        res.sort((a, b) =>
                            distributionReportRelevantStatHierarchy.indexOf(a.status) - distributionReportRelevantStatHierarchy.indexOf(b.status)
                        );
                    } else {
                        res.sort((a, b) =>
                            distributionReportRelevantStatHierarchy.indexOf(b.status) - distributionReportRelevantStatHierarchy.indexOf(a.status)
                        );
                    }
                    break;
                default:
                // Name
                    if (sortOrder === SortOrderEnum.descending) {
                        res.sort((a, b) => b.email.toLocaleLowerCase().localeCompare(a.email.toLocaleLowerCase()));
                    }
                    break;
            }

            // deal with paging
            res = res?.slice((pageCriteria.page - 1) * pageCriteria.pageSize, pageCriteria.page * pageCriteria.pageSize);

            return res;
        }),

        shareReplay(1)
    );

    constructor(
        private statisticsReportService: StatisticsReportService
    ) {
        super();
        this.setFilters([this.emailFilter]);
    }

    getSortableFields(): Observable<any[]> {
        return of(this.sortFields);
    }

    getSortOrder(): Observable<SortOrderEnum> {
        return this.sortOrderSubject.asObservable();
    }

    getSortField(): Observable<any> {
        return this.sortFieldSubject.asObservable();
    }

    setSortOrder(order: SortOrderEnum): void {
        this.sortOrderSubject.next(order);
    }

    setSortField(field: any): void {
        this.sortFieldSubject.next(field);
    }

    getItemCount(): Observable<number> {
        return this.nResultSubject.asObservable();
    }

    /**
     * Gets the current page criteria
     */
    getPageCriteria(): Observable<PageCriteriaModelInterface> {
        return this.pageCriteriaSubject.asObservable();
    };

    /**
     * Updates the total of pages
     */
    setMaxPageCriteria(totalNumberOfPages: number): void {

    };
    /**
     * Updates the page criteria
     */
    setPageCriteria(pageCriteria: PageCriteriaModelInterface): void {
        this.pageCriteriaSubject.next(pageCriteria);
    };

    /**
     * Checks that the page criteria can be updated
     * @NOTE This is an observable so that users can make the decision via dialogs etc.
     */
    canSetPageCriteria(): Observable<boolean> {
        return of(true);
    }

    setStatType(statType: DistributionReportStat): void {
        this.statTypeSubject.next(statType);
    }

    setSelectedIndividualRecipient(data: IndividualRecipientReportDataModel): void {
        this.selectedIndividualRecipientSubject.next(data ?? null);
    }

    filterByStatType(recipients: EmailRecipientModel[], statType: DistributionReportStat): EmailRecipientModel[] {
        if (!recipients?.length) {
            return [];
        }

        switch (statType) {
            case DistributionReportStat.DELIVERED:
                recipients = recipients.filter(item =>
                    item.stats.includes(DistributionReportStat.DELIVERED)
                    || item.stats.includes(DistributionReportStat.RELIABLE_RECIPIENTS)
                    || item.stats.includes(DistributionReportStat.MPP_RECIPIENTS)
                );
                break;
            case DistributionReportStat.UNIQUE_OPEN:
                recipients = recipients.filter(item =>
                    item.stats.includes(DistributionReportStat.UNIQUE_OPEN)
                        || item.stats.includes(DistributionReportStat.RELIABLE_UNIQUE_OPEN)
                        || item.stats.includes(DistributionReportStat.MPP_OPEN)
                );
                break;
            case DistributionReportStat.UNIQUE_CLICKS:
                recipients = recipients.filter(item =>
                    item.stats.includes(DistributionReportStat.UNIQUE_CLICKS)
                        || item.stats.includes(DistributionReportStat.RELIABLE_UNIQUE_CLICKS)
                        || item.stats.includes(DistributionReportStat.MPP_CLICKS)
                );
                break;
            case DistributionReportStat.REPLY:
                recipients = recipients.filter(item =>
                    item.stats.includes(DistributionReportStat.REPLY));
                break;
            case DistributionReportStat.UNOPENED:
                recipients = recipients.filter(item =>
                    (item.stats.includes(DistributionReportStat.DELIVERED)
                    || item.stats.includes(DistributionReportStat.RELIABLE_RECIPIENTS)
                    || item.stats.includes(DistributionReportStat.MPP_RECIPIENTS))
                    && !(
                        item.stats.includes(DistributionReportStat.UNIQUE_OPEN)
                        || item.stats.includes(DistributionReportStat.RELIABLE_UNIQUE_OPEN)
                        || item.stats.includes(DistributionReportStat.MPP_OPEN)
                    )
                );

                break;
            case DistributionReportStat.SOFT_BOUNCED:
                recipients = recipients.filter(item => item.stats.includes(DistributionReportStat.SOFT_BOUNCED));
                break;
            case DistributionReportStat.BLOCKED:
                recipients = recipients.filter(item =>
                    item.stats.includes(DistributionReportStat.SOFT_BOUNCED)
                    || item.stats.includes(DistributionReportStat.HARD_BOUNCED)
                    || item.stats.includes(DistributionReportStat.REJECT)
                    || item.stats.includes(DistributionReportStat.PREVIOUS_UNSUB)
                    || item.stats.includes(DistributionReportStat.PREVIOUS_SOFT_BOUNCED)
                    || item.stats.includes(DistributionReportStat.PREVIOUS_SPAM_REPORT)
                    || item.stats.includes(DistributionReportStat.PREVIOUS_HARD_BOUNCED)
                );
                break;
            case DistributionReportStat.UNSUBSCRIBED:
                recipients = recipients.filter(item =>
                    item.stats.includes(DistributionReportStat.UNSUBSCRIBED)
                );
                break;
            case DistributionReportStat.HARD_BOUNCED:
                recipients = recipients.filter(item =>
                    item.stats.includes(DistributionReportStat.HARD_BOUNCED)
                );
                break;
            case DistributionReportStat.SPAM_REPORT:
                recipients = recipients.filter(item =>
                    item.stats.includes(DistributionReportStat.SPAM_REPORT)
                );
                break;
        }

        return recipients;
    }
}
